import dayjs from 'dayjs';
import { PredefinedColors } from '@ionic/core';
import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import {
    KEYS_WITH_MEMBERS,
    ICareMap,
    IContactMethod,
    IHouseholdMember,
    INewCareMap,
    IPatient,
    ISupportGroup,
    ISupportGroupKey,
    ISupportGroupMember,
    SUPPORT_GROUP_KEYS,
    IChecklist,
    ICaremapAccess,
    IContactMethodType,
    IWizardAnswer,
    IRelationship,
} from './ICareMap';
import { CareMapFactory } from './CareMapFactory';
import { DefaultAvatarProvider } from '../avatar/DefaultAvatarProvider';
import { FlowDownloader } from './FlowDownloader';
import { MAX_HOUSEHOLD_MEMBERS } from '../nodes/NodeAdapter';
import { ILabelKey, LabelProvider } from '../config/LabelProvider';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal } from '../auth/AppPrincipal';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../support/AsyncStorageProvider';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { WelcomePageState } from '../QuestionnaireWelcomePage';
import { AlertPresenter } from '../AlertBinder';
import { EnvConfiguration } from '../config/EnvConfiguration';
import { IOrganizationUser } from '../organizations/IOrganization';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { WithLinks } from './WithLinks';
import { SupportGroupKeySorter } from './SupportGroupKeySorter';
import { MAX_HONEYCOMB_SIZE } from '../nodes/HoneycombConfigProvider';
import { CompositeAnonymousHttpClient } from '../auth/anonymous/CompositeAnonymousHttpClient';
import { UserState, IUser } from '../user/UserState';
import { Logger } from '../support/Logger';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { CareMapMerger } from './CareMapMerger';
import { backfillStructuredRecordIds } from './backfillStructuredRecordIds';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { IdentityFieldTranslator } from '@innerhive-internal/innerhive-common';

// At some point, we likely will want userIsPatient to be a more first class
// property on caremap... but for now, it is stored in the exiting
//   initialWizardAnswers['recipient'] as if they choose Myself
// I did this because it was expedient (making use of infrastructure already in place)
//
// It is encapsulated by CareMapState.isUserThePatient(): boolean
//
// and the const that follows encapsulates what the value should be

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';

export const MYSELF = { value: 'myself', label: 'Myself' };

export interface ISupportGroupLabels {
    title: string;
    memberTitle: string;
    shortMemberTitle: string;
    memberNameLabel: string;
    roleLabel: string;
    rolePlaceholder: string;
};

type ISupportGroupMembersMap = {[key in ISupportGroupKey]: ISupportGroupMember[] | undefined};

export class CareMapState extends State<Record<string, never>> {
    static inject = () => [
        APIClient,
        CareMapFactory,
        FlowDownloader,
        LabelProvider,
        MixpanelService,
        AppPrincipal,
        AsyncStorageProvider,
        BufferedEventBus,
        HistoryViewModel,
        WelcomePageState,
        AlertPresenter,
        EnvConfiguration,
        SupportGroupKeySorter,
        CompositeAnonymousHttpClient,
        UserState,
        Logger,
        CareMapMerger,
        IdentityFieldTranslator,
        DefaultAvatarProvider
    ];

    constructor(
        private apiClient: APIClient,
        private factory: CareMapFactory,
        private flowDownloader: FlowDownloader,
        private labelProvider: LabelProvider,
        private tracking: MixpanelService,
        private principal: AppPrincipal,
        private storageProvider: AsyncStorageProvider,
        private events: BufferedEventBus,
        private history: HistoryViewModel,
        private welcomePageState: WelcomePageState,
        private alert: AlertPresenter,
        private envConfig: EnvConfiguration,
        private keySorter: SupportGroupKeySorter,
        private anonymousHttpClient: CompositeAnonymousHttpClient,
        private userState: UserState,
        private logger: Logger,
        private merger: CareMapMerger,
        private identityTranslator: IdentityFieldTranslator,
        private defaultAvatarProvider: DefaultAvatarProvider
    ) {
        super({});

        // if the caremap is for the user, then set up labels
        // to use the MY_CAREMAP__ prefix when possible
        // so we can thing like ""...your caremap" instead of "...Lance's caremap"
        this.subscribe(() => {
            if (this.isUserThePatient() !== this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable){
                this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = this.isUserThePatient();
            }
        });

        this.events.on('UserState.deleted', () => {
            this.storageProvider.getStringProvider('current-caremap-id').remove();
        });

        this.events.on('web-sockets.message', this.onWebsocketMessage);

        this.events.on('UserState.identity-changed', (payload?: { userId: string; user: IUser }) => {
            if (payload) {
                this.applyIdentityToLinkedMembers(payload.userId, payload.user);
            }
        });

        this.lastActiveCareMapIdStorage = this.storageProvider.getStringProvider('last-active-caremap-id');
    }

    private lastActiveCareMapIdStorage: IAsyncSmartStorageProvider<string>;
    private lastActiveCareMapId?: string;

    private pendingServerRefresh = false;

    private onWebsocketMessage = async (message?: IResourceChangedSocketMessage) => {
        if (!message) {
            return;
        }
        const target = message.resourceToRefresh.toLowerCase();
        const activeHref = this._activeCaremap?.links?.self?.href?.toLowerCase();

        if (activeHref && target === activeHref) {
            if (this.isSaved()) {
                await this.fetchAndMergeServerState();
            } else {
                this.pendingServerRefresh = true;
            }
            return;
        }

        // A non-active loaded caremap changed (e.g. the user's own caremap while they view another).
        // Refresh its cached copy so the switcher reflects identity changes pushed from another device.
        // No 3-way merge needed — there are no local edits to a non-active caremap.
        const stale = this.careMaps.find(m => m?.links?.self?.href?.toLowerCase() === target && m.careMapId !== this._activeCaremap?.careMapId);
        if (stale) {
            await this.refreshNonActiveCaremap(stale);
        }
    };

    private refreshNonActiveCaremap = async (stale: ICareMap) => {
        try {
            const server = await this.apiClient.get(stale.links.self, CAREMAP_MEDIA_TYPE);
            // Ignore an eventually-consistent stale read older than the cached copy (would briefly
            // flash an out-of-date name in the switcher until the next push corrects it).
            if (server.version < stale.version) {
                return;
            }
            this.careMaps = this.careMaps.map(m => m.careMapId === server.careMapId ? server : m);
            this.setState({});
        } catch (err: any) {
            // a transient/aborted fetch is expected (best-effort refresh); alert only on genuine failures
            if (!isExpectedFetchError(err)) {
                this.logger.error(`CareMapState.refreshNonActiveCaremap failed: ${err.toString()}`);
            }
        }
    };

    private fetchAndMergeServerState = async () => {
        try {
            const server = await this.apiClient.get(this._activeCaremap!.links.self, CAREMAP_MEDIA_TYPE);
            // The active caremap can be cleared (unsetCurrentCareMap, on logout / navigation / switch)
            // while this fetch is in flight; if so there's nothing to merge the response into, and the
            // merge would dereference undefined (reading 'patient'). Bail — nothing to reconcile.
            if (!this._activeCaremap) {
                return;
            }
            // Ignore an eventually-consistent stale read: a re-fetch whose version is older than
            // what we last saved would overwrite just-saved values with stale ones (the merge
            // trusts `server` for any field where local === base, which holds right after a save).
            // A later push — or the next save — reconciles with the current server state.
            if (this.lastApiResponse && server.version < this.lastApiResponse.version) {
                return;
            }
            const merged = this.merger.merge({
                base: this.lastApiResponse,
                local: this._activeCaremap as ICareMap,
                server
            });
            this.lastApiResponse = server;
            this._activeCaremap = { ...merged, patient: backfillStructuredRecordIds(merged.patient) };
            this.lastPatchedState = undefined; // force re-evaluation on next save
            this.careMaps = this.careMaps.map(map => server.careMapId === map.careMapId ? merged : map);
            this.setState({});
        } catch (err: any) {
            // a transient/aborted caremap fetch is expected for this best-effort refresh; it retries
            // on the next trigger. Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.logger.error(`CareMapState.fetchAndMergeServerState failed: ${err.toString()}`);
            }
        }
    };

    // Force a fresh fetch of the active caremap and merge it into state (no cache, no wholesale
    // replace, no loaded-flag reset). Used by the foreground/reconnect catch-up so changes made by
    // co-owners while we were away converge without a full reload. Scope is intentionally the active
    // caremap only — matches the story and keeps the reload cheap.
    reload = async (careMapId: string): Promise<void> => {
        if (!this._activeCaremap || this._activeCaremap.careMapId !== careMapId) {
            // only the active caremap is reconciled; a stale id (e.g. user switched) is a no-op
            return;
        }

        if (!this._activeCaremap.links?.self) {
            // a brand-new unsaved caremap has no self link yet; nothing on the server to reconcile
            return;
        }

        await this.fetchAndMergeServerState();
    };

    private maybeRefreshFromServer = () => {
        if (this.pendingServerRefresh) {
            this.pendingServerRefresh = false;
            this.fetchAndMergeServerState();
        }
    };

    // injected at IOC due to circular reference
    _orgState!: OrganizationsState;

    private _activeCaremap?: ICareMap | INewCareMap;

    // hold all care maps and expose helper methods
    private careMaps: ICareMap[] = [];

    getCareMaps = (): ICareMap[] => {
        return this.careMaps;
    };

    getCareMapById = (careMapId: string) => {
        return this.careMaps.find(map => map.careMapId === careMapId);
    };

    getIncompleteCareMap = (): ICareMap | undefined => {
        return this.careMaps.find(map => this.isIncomplete(map));
    };

    // returns true if hasn't gone past the Care Recipient Name step in the mini-wizard
    // NOTE: doesn't apply to Organizations
    isIncomplete = (careMap: ICareMap | INewCareMap) => {
        return !this._orgState.isOrgTeamMember() && !careMap.essentialMapComplete && !careMap.patient.firstName && !careMap.patient.lastName;
    };

    // true has real profile data extracted by a prior recording
    hasExtractedData = (careMap: ICareMap | INewCareMap) => {
        const p = careMap.patient;
        return (p.diagnoses?.length ?? 0) > 0
            || (p.allergies?.length ?? 0) > 0
            || (p.medications?.length ?? 0) > 0
            || Boolean(p.address);
    };

    getAddCareRecipientButtonState = (careMap?: ICareMap | INewCareMap): 'add-care-recipient' | 'set-up-care-profile' | 'complete-care-profile' => {
        if (careMap && this.isIncomplete(careMap) && this.hasExtractedData(careMap)) {
            return 'complete-care-profile';
        }
        if (careMap && this.isIncomplete(careMap)) {
            return 'set-up-care-profile';
        }
        return 'add-care-recipient';
    };

    // returns true if there is a caremap other than the current one that is MYSELF
    hasOtherCareMapForSelf = (): boolean => {
        const selfMap = this.careMaps.find(map => map.userId === this.principal.userId && map.initialWizardAnswers['recipient']?.value === MYSELF.value);
        return Boolean(selfMap && selfMap.careMapId !== this._activeCaremap?.careMapId);
    };

    hasAnyCareMapForSelf = (): boolean => {
        const selfMap = this.careMaps.find(map => map.userId === this.principal.userId && map.initialWizardAnswers['recipient']?.value === MYSELF.value);
        return Boolean(selfMap);
    };

    hasCareMaps = (): boolean => {
        return this.getCareMaps().length > 0;
    };

    private _ensureCareMapPromise?: Promise<void>;

    ensureCareMapExists = () => {
        if (this.hasCareMaps()) {
            this._ensureCareMapPromise = undefined;
            return Promise.resolve();
        }
        if (!this._ensureCareMapPromise) {
            this._ensureCareMapPromise = this.addNewCaremapV3({
                patient: {},
                isRecipientSelf: false,
                isAutoCreated: true
            }).then(async ({ careMapId }) => {
                await this.setActiveCaremap(careMapId);
            }).finally(() => {
                this._ensureCareMapPromise = undefined;
            });
        }
        return this._ensureCareMapPromise;
    };

    private getCareMapFromOrg = async (careMapId: string) => {
        const orgCaremapLite = this._orgState.getOrganizationCareMap(careMapId);
        if (orgCaremapLite?.links.self) {
            return await this.apiClient.get(orgCaremapLite?.links.self, CAREMAP_MEDIA_TYPE);
        } else {
            return undefined;
        }
    };

    getOrLoadCareMap = async (careMapId: string) => {
        let map: ICareMap | INewCareMap | undefined = this.getCareMapById(careMapId);
        if (!map){
            map = await this.getCareMapFromOrg(careMapId);
        }
        if (!map) {
            const error: any = Error(`Care Map with ID ${careMapId} not found (or don't have access)`);
            error.errorCode = 'not-found';
            throw error;
        }
        return map;
    };

    setActiveCaremap = async (careMapId: string) => {
        // if it is already active then do nothing
        // because otherwise we might stomp on changes in thereAdd commentMore actions
        // eg delete support group is waiting for its changes to save while redirecting
        if (this._activeCaremap?.careMapId === careMapId){
            return;
        }

        // WARNING: this block is identical to getOrLoadCareMap() above, but invoking it here inserts
        // an await into the main flow, creating a race condition that breaks the onboarding wizard
        let map: ICareMap | INewCareMap | undefined = this.getCareMapById(careMapId);
        if (!map){
            map = await this.getCareMapFromOrg(careMapId);
        }
        if (!map) {
            const error: any = Error(`Care Map with ID ${careMapId} not found (or don't have access)`);
            error.errorCode = 'not-found';
            throw error;
        }

        this._activeCaremap = { ...map, patient: backfillStructuredRecordIds(map.patient) };
        if (map.careMapId) {
            this.lastApiResponse = JSON.parse(JSON.stringify(map)) as ICareMap;
        }
        this.setState({ });

        this.storageProvider.getStringProvider('current-caremap-id').set(careMapId);

        this.lastActiveCareMapId = careMapId;
        this.lastActiveCareMapIdStorage.set(careMapId);

        // Reconcile the newly-active caremap with the server so any identity changes that arrived
        // via realtime while we were viewing another caremap are picked up immediately. Mirror the
        // same in-flight-save guard used by onWebsocketMessage: if a save is in progress, set the
        // pending flag and let maybeRefreshFromServer (called when the save completes) do the fetch.
        if (map.careMapId) {
            if (this.isSaved()) {
                this.fetchAndMergeServerState().catch(() => {
                    // fetchAndMergeServerState already logs genuine failures; swallowing here keeps
                    // the switch best-effort — the user still sees the cached caremap on error.
                });
            } else {
                this.pendingServerRefresh = true;
            }
        }
    };

    unsetCurrentCareMap = async () => {
        this._activeCaremap = undefined;
        this.setState({ });
        await this.storageProvider.getStringProvider('current-caremap-id').remove();
    };

    getMostRecentCaremap = (): ICareMap | undefined => {
        if (this.getActiveCaremap()){
            return this.getActiveCaremap() as ICareMap;
        }
        if (this.lastActiveCareMapId && this.getCareMapById(this.lastActiveCareMapId)){
            return this.getCareMapById(this.lastActiveCareMapId);
        }
        return this.getCareMaps()[0];
    };

    hasActiveCareMap = () => {
        return Boolean(this.getActiveCaremap());
    };

    getActiveCaremap = (): ICareMap | INewCareMap| undefined => {
        return this._activeCaremap;
    };

    hasCompletedCareMap = () => {
        return this.getCareMaps().some(map => map.essentialMapComplete === true);
    };

    getLabel = (labelName: ILabelKey, placeholders: any = {}) => {
        return this.labelProvider.getLabel(labelName, { careRecipient: this.getPatientFirstName(), ...placeholders });
    };

    hasStartedWizard = () => Boolean(this._activeCaremap?.careMapId);

    deleteAndRestart = async () => {
        if (this._activeCaremap?.links){
            await this.apiClient.delete(this._activeCaremap.links.self);
            window.location.hash = '';
            window.location.reload();
        }
    };

    loadWithoutActive = async () => {
        if (this.principal.tokenType === 'caremap-viewer'){
            if (this.careMaps.length > 0){
                // the implementation below `loadUsingAnonymousSessions`
                // is using a cache from app start, and so this won't refresh
                // it is reasonably we might call this expecting it to refresh
                // I confirmed that we don't currently in the anonymous UX
                // but put this DX guard in place so that if we do, we are driven to here
                // at that point we basically want
                // 1. on first call, use the cache (to avoid fetching them all again)
                // 2. on subsequents calls, just refresh each using their self links, and
                //      CompositeAnonymousHttpClient will use the correct link... BUT this might be
                //      when we discover the JWT for that caremap has expired
                throw new Error('calling CareMapState.loadWithoutActive more than once is not yet supported for an anonymous session');
            }
            await this.loadUsingAnonymousSessions();
        } else {
            const entry = await this.apiClient.entry();
            this.careMaps = await this.apiClient.get(entry.links.careMaps, CAREMAP_MEDIA_TYPE);
            this.lastActiveCareMapId = await this.lastActiveCareMapIdStorage.get();
            this.setState({});

            this.profileTracking();
        }
    };

    private profileTracking = () => {
        this.events.emit('MixPanelService.care-recipient-relationships', this.getUserBelongingRelationships());

        // 'Care Recipient Profiles'
        this.tracking.setProfileProp({prop: 'Care Recipient Profiles', val: Number(this.hasAnyCareMapForSelf())});

        // 'Caregiver Profiles'
        this.tracking.setProfileProp({prop: 'Caregiver Profiles', val: this.caregiverProfileCount()});
    };

    // After a successful caremap PUT, propagates the edited member's identity fields back to
    // UserState (LOCAL state only — deliberately NO PUT to /user) and to the user's OTHER linked
    // member nodes across all loaded caremaps. Only fires when the edited member is the current user.
    //
    // DO NOT add a userState.save() / PUT here (this has been mistakenly "fixed" before). The account
    // is ALREADY persisted server-side by the very same caremap PUT: the API's
    // syncLinkedMemberEditsToAccounts applies the edited member's identity to the linked account
    // (updateUserInternal) and enqueues a websocket to refresh Account Settings. This client update is
    // purely the OPTIMISTIC layer — instant local feedback so the user doesn't wait for that websocket
    // round-trip. On reload the client reads the server-persisted account, so the change survives with
    // no client PUT. Adding one would be redundant AND would race the server's own account write for
    // the same edit.
    //
    // Loop-safety: does NOT call userState.save() and does NOT emit 'UserState.identity-changed'
    // (that event is for real account saves and would re-trigger applyIdentityToLinkedMembers).
    // applyIdentityToLinkedMembers is called here directly for the cross-caremap propagation;
    // since the active caremap already holds the saved member data, it is a no-op for that caremap
    // (translator's `changed` flag short-circuits re-application of identical values).
    private applyLinkedMemberIdentityBackToAccount = (merged: ICareMap, base: ICareMap | undefined): void => {
        const userId = this.userState.state.userId;
        if (!userId) {
            return;
        }

        // Collect all members from the merged caremap across every section.
        const linkedMembers: Array<{ member: IPatient | IHouseholdMember | ISupportGroupMember; section: 'patient' | 'household' | string }> = [];

        if (merged.patient.linkedUserId === userId) {
            linkedMembers.push({ member: merged.patient, section: 'patient' });
        }
        for (const m of merged.household.members) {
            if (m.linkedUserId === userId) {
                linkedMembers.push({ member: m, section: 'household' });
            }
        }
        for (const key of SUPPORT_GROUP_KEYS) {
            for (const m of merged[key].members) {
                if (m.linkedUserId === userId) {
                    linkedMembers.push({ member: m, section: key });
                }
            }
        }

        if (linkedMembers.length === 0) {
            return;
        }

        // Drive the account from the member the user actually EDITED — the linked member whose
        // translated identity changed vs the pre-save server baseline (`lastApiResponse`). The
        // previous version accumulated across ALL linked members and let the last one win, so a
        // stale sibling (a second linked node in this caremap carrying a different name) could
        // hijack the account and fan its name back over the edit. Comparing each member's
        // translated identity to its baseline isolates the one the user changed.
        // `identityOf` is an account-relative probe: equal iff both members would push the account
        // to the same state — sufficient here because each member is compared against its own
        // baseline through the same account, so any real identity delta between them surfaces.
        const identityOf = (m: IPatient | IHouseholdMember | ISupportGroupMember, section: string): string => {
            return JSON.stringify(this.identityTranslator.applyToAccount(this.userState.state, m, { section }).user);
        };
        const edited = linkedMembers.find(({ member, section }) => {
            const baseMember = this.findBaseMember(base, section, member);
            return !baseMember || identityOf(member, section) !== identityOf(baseMember, section);
        });

        // Track a plain IUser for the translator call; the final setState merges it back onto the
        // full MaybeWithLinks<IUser> state so links and non-identity fields are preserved.
        let identityFields: IUser = this.userState.state;
        if (edited) {
            const { user: next, changed } = this.identityTranslator.applyToAccount(identityFields, edited.member, { section: edited.section });
            if (changed) {
                identityFields = next;
                this.userState.setState({ ...this.userState.state, ...identityFields });
            }
        }

        // Propagate the resolved identity to OTHER loaded caremaps' linked member nodes (and the
        // active one, where it short-circuits as a no-op since it already holds the saved data).
        this.applyIdentityToLinkedMembers(userId, identityFields);
    };

    // Finds the pre-save (baseline) version of a member in `base`, used to detect which linked
    // member the user actually edited. The patient has no id; other members match by id within
    // their own section (ids are not guaranteed unique across sections).
    private findBaseMember = (
        base: ICareMap | undefined,
        section: string,
        member: IPatient | IHouseholdMember | ISupportGroupMember
    ): IPatient | IHouseholdMember | ISupportGroupMember | undefined => {
        if (!base) {
            return undefined;
        }
        if (section === 'patient') {
            return base.patient;
        }
        const memberId = 'id' in member ? member.id : undefined;
        if (section === 'household') {
            return base.household.members.find(m => m.id === memberId);
        }
        for (const key of SUPPORT_GROUP_KEYS) {
            if (key === section) {
                return base[key].members.find(m => m.id === memberId);
            }
        }
        return undefined;
    };

    // Applies the given user's identity fields to every linked member node (across all loaded
    // caremaps, including the active one) whose linkedUserId matches userId. Iterates ALL sections
    // — patient, household, and every support-group key — so no surface is missed.
    // Called from the UserState.identity-changed event handler; does not require a direct
    // CareMapState ↔ UserState injection (which would be circular).
    private applyIdentityToLinkedMembers = (userId: string, user: IUser): void => {
        let anyChanged = false;

        for (const careMap of this.collectDistinctCareMaps()) {
            const updatedCareMap = this.applyIdentityToOneCaremap(careMap, userId, user);
            if (updatedCareMap !== careMap) {
                anyChanged = true;
                this.careMaps = this.careMaps.map(m => m.careMapId === careMap.careMapId ? updatedCareMap : m);
                if (this._activeCaremap?.careMapId === careMap.careMapId) {
                    this._activeCaremap = updatedCareMap;
                }
            }
        }

        if (anyChanged) {
            this.setState({});
        }
    };

    // Returns a deduplicated list of all loaded caremaps — _activeCaremap can be the same object
    // reference as an entry in careMaps, so we key by careMapId to avoid double-applying.
    private collectDistinctCareMaps = (): ICareMap[] => {
        const seen = new Set<string>();
        const result: ICareMap[] = [];
        for (const map of [this._activeCaremap, ...this.careMaps]) {
            const careMapId = map?.careMapId;
            if (!map || !careMapId || seen.has(careMapId)) {
                continue;
            }
            seen.add(careMapId);
            // _activeCaremap may be INewCareMap; the careMapId guard above ensures only completed ICareMap instances reach here.
            result.push(map as ICareMap);
        }
        return result;
    };

    // Applies user identity fields to every linked member (patient + household + support groups)
    // in a single caremap. Returns the original reference when nothing changed (identity check).
    private applyIdentityToOneCaremap = (careMap: ICareMap, userId: string, user: IUser): ICareMap => {
        let updated = careMap;

        if (careMap.patient.linkedUserId === userId) {
            const { member: updatedPatient, changed } = this.identityTranslator.applyToMember(careMap.patient, user, 'patient');
            if (changed) {
                updated = { ...updated, patient: updatedPatient };
            }
        }

        let householdChanged = false;
        const updatedHouseholdMembers = updated.household.members.map(m => {
            if (m.linkedUserId !== userId) {
                return m;
            }
            const { member: updatedMember, changed } = this.identityTranslator.applyToMember(m, user, 'household');
            if (changed) {
                householdChanged = true;
            }
            return changed ? updatedMember : m;
        });
        if (householdChanged) {
            updated = { ...updated, household: { ...updated.household, members: updatedHouseholdMembers } };
        }

        for (const key of SUPPORT_GROUP_KEYS) {
            let groupChanged = false;
            const updatedGroupMembers = updated[key].members.map(m => {
                if (m.linkedUserId !== userId) {
                    return m;
                }
                const { member: updatedMember, changed } = this.identityTranslator.applyToMember(m, user, key);
                if (changed) {
                    groupChanged = true;
                }
                return changed ? updatedMember : m;
            });
            if (groupChanged) {
                updated = { ...updated, [key]: { ...updated[key], members: updatedGroupMembers } };
            }
        }

        return updated;
    };

    private getMeAsMembers = () => {
        let members: IHouseholdMember[] = [];
        this.careMaps.forEach(map => {
            members = members.concat(map.household.members.filter(m => this.isMyMember(m)));
        });
        return members;
    };

    private caregiverProfileCount = () => {
        let numMaps = 0;
        this.getMeAsMembers().forEach(member => {
            numMaps += member.primaryCaregiver ? 1 : 0;
        });
        return numMaps;
    };

    private getUserBelongingRelationships = () => {
        return this.getMeAsMembers().map(member => member.relationship);
    };

    private loadUsingAnonymousSessions = async () => {
        this.careMaps = this.anonymousHttpClient.getInitCaremaps();
        this.setState({ });
    };

    private adaptCareMapForApi = (careMap: ICareMap | INewCareMap): ICareMap => {
        // remove photoDataURI b/c we never want to persist that to the API
        // set notes to empty array because those aren't saved by PUT Caremap

        const copy: ICareMap = JSON.parse(JSON.stringify(careMap));
        delete copy.patient.photoDataURI;

        for (const section of KEYS_WITH_MEMBERS) {

            for (const member of copy[section].members){
                if (section === 'household') {
                    delete (member as IHouseholdMember).photoDataURI;
                }

            }
        }
        return copy;
    };

    private savingPromise?: Promise<void>;
    private isSaveEnqueued = false;
    private is409Retrying = false;

    isSaved = () => !this.savingPromise && !this.isSaveEnqueued;

    enqueueSave = (): void => {
        // if there is a save in flight, then enqueue for when they are all done
        if (this.savingPromise){
            this.isSaveEnqueued = true;
        }
        // otherwise, do it now
        else {
            this.dequeueSave();
        }
    };

    enqueuedSaveError?: any;

    private setEnqueuedSaveError = (err: any) => {
        this.enqueuedSaveError = err;

        // emit so consumers know to check enqueuedSaveError
        this.setState({});
    };

    clearEnqueuedSaveError = () => {
        if (this.enqueuedSaveError){
            this.enqueuedSaveError = undefined;

            // emit so consumers know to check enqueuedSaveError
            this.setState({ });
        }
    };

    private dequeueSave = () => {
        // NOTE: maybe we shouldn't try and save while there is an error
        // or maybe the latest state would clear the error...
        // currently we still let it save b/c likely it will result in the same error
        // which does little harm, but it might clear the error

        this.isSaveEnqueued = false;

        // NOTE: important that we sync start a save, so that this.savingPromise gets set
        this.save().then(() => {
            this.clearEnqueuedSaveError();
            this.maybeRefreshFromServer();
        }).catch(err => {
            this.setEnqueuedSaveError(err);
            this.maybeRefreshFromServer();
        });
    };

    private lastPatchedState?: string = undefined;

    private save = async (): Promise<void> => {
        // if we already saving, then queue it up for when that one is done
        if (this.savingPromise){
            return this.savingPromise
                .catch(err => {
                    // ignore an error on the previous save, it is
                    // that callers responsibility to handle that failure
                })
                .then(() => this.save());
        }

        this.savingPromise = Promise.resolve().then(async () => {
            try {
                const careMap = this.getActiveCaremap();
                if (!careMap){
                    // TODO: we should still save, but we no longer have the memory reference to save
                    return;
                }
                if (!careMap.careMapId) {
                    // this creates a copy of the body, so when we save this.lastPatchedState it reflects what we actually sent
                    const finalBody = this.adaptCareMapForApi(careMap);
                    const headers = {
                        accept: CAREMAP_MEDIA_TYPE,
                        'content-type': CAREMAP_MEDIA_TYPE
                    };

                    // API errors out otherwise
                    // we're only temporarily using to get the appropriate POST link
                    delete finalBody.links;

                    const entry = await this.apiClient.entry();
                    const result = await this.apiClient.post(
                        entry.links.careMaps,
                        finalBody,
                        headers
                    );

                    this.lastApiResponse = result;
                    this.lastPatchedState = JSON.stringify({ ...finalBody, version: undefined });

                    // add the new care map to our collection
                    this.careMaps.push(result);
                    this.setActiveCaremap(result.careMapId);
                } else {
                    const body = {
                        ...careMap,
                    };

                    // this creates a copy of the body, so when we save this.lastPatchedState it reflects what we actually sent
                    const finalBody = this.adaptCareMapForApi(body);

                    // don't save if nothing changed
                    if (this.lastPatchedState !== JSON.stringify({ ...finalBody, version: undefined })){
                        const result = await this.apiClient.put(
                            careMap,
                            finalBody,
                            {
                                accept: CAREMAP_MEDIA_TYPE,
                                'content-type': CAREMAP_MEDIA_TYPE
                            }
                        );

                        // Snapshot the pre-save baseline before reassigning lastApiResponse below —
                        // applyLinkedMemberIdentityBackToAccount needs the PRE-save state to detect
                        // which member was edited (the post-save response already contains the edit).
                        const preSaveBase = this.lastApiResponse;
                        const merged = this.merger.merge({
                            base: preSaveBase,
                            local: this._activeCaremap! as ICareMap,
                            server: result
                        });
                        this.lastApiResponse = result;
                        this._activeCaremap = { ...merged, patient: backfillStructuredRecordIds(merged.patient) };
                        this.lastPatchedState = JSON.stringify({ ...finalBody, version: undefined });
                        this.careMaps = this.careMaps.map(map => result.careMapId === map.careMapId ? merged : map);
                        this.setState({ });
                        this.applyLinkedMemberIdentityBackToAccount(merged, preSaveBase);
                    }
                }
            } catch (e: any) {
                if (e.status === 409) {
                    if (this.is409Retrying) {
                        this.is409Retrying = false;
                        e.displayMessage = 'Sorry, this care map has already been updated. Please refresh and try again.';
                        throw e;
                    }

                    try {
                        const server = await this.apiClient.get(this._activeCaremap!.links.self, CAREMAP_MEDIA_TYPE);
                        const merged = this.merger.merge({
                            base: this.lastApiResponse,
                            local: this._activeCaremap! as ICareMap,
                            server
                        });
                        this.lastApiResponse = server;
                        this._activeCaremap = { ...merged, patient: backfillStructuredRecordIds(merged.patient) };
                        this.pendingServerRefresh = false;
                        this.lastPatchedState = undefined; // force re-save
                        this.careMaps = this.careMaps.map(map => server.careMapId === map.careMapId ? merged : map);
                        this.setState({});

                        // Clear savingPromise so save() can re-enter, then retry once
                        this.savingPromise = undefined;
                        this.is409Retrying = true;
                        return this.save().finally(() => {
                            this.is409Retrying = false;
                        });
                    } catch (recoveryError: any) {
                        this.is409Retrying = false;
                        recoveryError.displayMessage = 'Sorry, this care map has already been updated. Please refresh and try again.';
                        throw recoveryError;
                    }
                }

                if (e.status === 404 && e.errorCode === 'caremap.deleted') {
                    const name = this.getPatientFirstName();
                    await this.alert.showAlertV2({
                        header: 'Care Map Deleted',
                        message: `Sorry... ${name ? `${name}'s` : 'This'} care map has been deleted in a different session, so you can no longer make changes.`
                    });

                    await this.afterDelete();
                    return;
                }

                throw e;
            }
        });

        return this.savingPromise.finally(() => {
            this.savingPromise = undefined;

            // if a save was enqueued, attempt to dequeue it
            if (this.isSaveEnqueued){
                this.dequeueSave();
            }
        });
    };

    delete = async () => {
        await this.apiClient.delete(this._activeCaremap?.links.self);

        const self = this._activeCaremap?.initialWizardAnswers['recipient-self'];
        this.tracking.track('Care Map Deleted', () => ({
            'Care Map State': this._activeCaremap?.essentialMapComplete ? 'completed' : 'questionnaire',
            'Care Recipient': self ? 'myself' : self === false ? 'someone else' : undefined
        }));

        // can only have one care map that is 'self'
        if (self) {
            // 'Care Recipient Profiles': 0
            this.events.emit('MixPanelService.set-profile-prop', {
                prop: 'Care Recipient Profiles',
                val: 0
            });
        }

        await this.afterDelete();
    };

    // Deletes a non-active caremap by id. Used by AddCareRecipientLauncher to roll back a caremap
    // it eagerly created when the voice flow aborts. Unlike delete(), this does not navigate or
    // touch the active caremap — the caller is responsible for restoring active state.
    deleteCaremapById = async (careMapId: string) => {
        const map = this.careMaps.find(m => m.careMapId === careMapId);
        if (!map) {
            return;
        }
        await this.apiClient.delete(map.links.self);
        this.careMaps = this.careMaps.filter(m => m.careMapId !== careMapId);
        this.setState({ });
    };

    private afterDelete = async () => {
        // delete the care map in this.careMaps and remove id from storage
        this.careMaps = this.careMaps.filter(map => this._activeCaremap?.careMapId !== map.careMapId);
        await this.storageProvider.getStringProvider('current-caremap-id').remove();

        if (!this.hasCareMaps()) {
            this.welcomePageState.setState({hasContinued: false});
        }

        this.history.replace('/');

        // Restore the "every signed-in user has a careMapKey" invariant for surfaces like
        // chat. Best-effort: if creation fails, the existing lazy callers (RecordingService,
        // Recorder, NewCareMapWizard) will retry next time they need a care map.
        this.ensureCareMapExists().catch(err => {
            this.logger.error(`afterDelete: ensureCareMapExists failed: ${err?.toString?.() ?? err}`);
        });
    };

    getHouseholdMembers = (): IHouseholdMember[] => {
        return this._activeCaremap!.household.members;
    };

    updateHouseholdMembers = (members: IHouseholdMember[]) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members: members.map(member => ({
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                })),
            }
        };
        this.setState({ });
        this.enqueueSave();
    };

    addHouseholdMembers = (newMembers: IHouseholdMember[]) => {
        try {
            if (this._activeCaremap!.household.members.length + newMembers.length > MAX_HOUSEHOLD_MEMBERS) {
                this.tracking.track('Tried to Add Too Many Members', () => ({
                    category: 'household'
                }));
                const error: any = new Error(`cannot add more than ${MAX_HOUSEHOLD_MEMBERS} inner circle members`);
                error.displayMessage = `Sorry, currently you cannot add more than ${MAX_HOUSEHOLD_MEMBERS} inner circle members.`;
                throw error;
            }

            for (const member of newMembers) {
                if (member.primaryCaregiver && this.isMyMember(member)) {
                    // 'Caregiver Profiles'
                    this.tracking.incrementProfileProp({prop: 'Caregiver Profiles', by: 1});
                }
            }

            const members = [
                ...this._activeCaremap!.household.members,
                ...newMembers.map(member => ({
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                }))
            ];

            this._activeCaremap = {
                ...this._activeCaremap!,
                household: {
                    ...this._activeCaremap!.household,
                    members,
                }
            };
            this.setState({ });
            this.enqueueSave();
        } catch (err) {
            // caller assumes enqueueSave and therefore isn/t expecting to deal with errors directly
            this.setEnqueuedSaveError(err);
        }
    };

    removeHouseholdMember = (memberId: string) => {
        console.log('REMOVE', memberId);
        const member = this._activeCaremap!.household.members.find(m => m.id === memberId);
        const members = this._activeCaremap!.household.members.filter(m => m.id !== memberId);
        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members
            }
        };
        this.setState({ });
        this.enqueueSave();

        if (member?.primaryCaregiver && this.isMyMember(member)) {
            // 'Caregiver Profiles'
            this.tracking.incrementProfileProp({prop: 'Caregiver Profiles', by: -1});
        }
    };

    // NOTE: if marking the map complete is needed in other updates, consider moving complete logic to enqueueSave?
    updatePatient = (patient: IPatient, options?: { markEssentialMapComplete: boolean; initialWizardAnswers: IWizardAnswer[] }) => {
        const payload = {
            ...this._activeCaremap!,
            patient: {
                ...patient,
                contactMethods: this.cleanupContactMethods(patient.contactMethods)
            }
        };

        if (options?.markEssentialMapComplete) {
            payload.essentialMapComplete = true;
        }

        if (options?.initialWizardAnswers) {
            let initialWizardAnswers = this._activeCaremap!.initialWizardAnswers || {};
            for (const answer of options.initialWizardAnswers) {
                initialWizardAnswers = {
                    ...initialWizardAnswers,
                    [answer.questionId]: answer.answer
                };
            }

            payload.initialWizardAnswers = initialWizardAnswers;
        }

        this._activeCaremap = payload;

        this.setState({ });
        this.enqueueSave();
    };

    updateHouseholdMember = (member: IHouseholdMember) => {
        console.log('UPDATE', member);
        const idx = this._activeCaremap!.household.members.findIndex(item => item.id === member.id);

        this._activeCaremap!.household.members[idx] = {
            ...member,
            contactMethods: this.cleanupContactMethods(member.contactMethods)
        };

        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members: this._activeCaremap!.household.members
            }
        };

        this.setState({ });
        this.enqueueSave();
    };

    getHouseholdMember = (memberId: string): IHouseholdMember => {
        const member = this.maybeGetHouseholdMember(memberId);
        if (!member) {
            // this really should never happen
            throw new Error(`Member with memberId ${memberId} not found`);
        }
        return member;
    };

    getMyHouseholdMember = (): IHouseholdMember | undefined => {
        return this.getCareMap().household.members.find(m => this.isMyMember(m));
    };

    maybeGetHouseholdMember = (memberId: string): IHouseholdMember | undefined => {
        return this.getCareMap().household.members.find(m => m.id === memberId);
    };

    answerInitialWizardQuestion = ({ questionId, answer }: IWizardAnswer) => {
        const initialWizardAnswers = this._activeCaremap!.initialWizardAnswers || {};

        this._activeCaremap = {
            ...this._activeCaremap!,
            initialWizardAnswers: {
                ...initialWizardAnswers,
                [questionId]: answer
            }
        };

        this.setState({ });
        this.enqueueSave();
    };

    getInitialWizardAnswer = (questionId: string): any => {
        const initialWizardAnswers = this._activeCaremap?.initialWizardAnswers || {};

        return initialWizardAnswers[questionId];
    };

    isCommunityExtracurricularRelevant = () => {
        return Boolean(this._activeCaremap?.initialWizardAnswers['community']);
    };

    isCommunityAdvocacyRelevant = () => {
        return Boolean(this._activeCaremap?.initialWizardAnswers['community2']);
    };

    addChecklists = (supportGroupKey: ISupportGroupKey, checklists: {templateId: string, html: string}[]) => {
        // all or nothing. if there are ANY duplicates, no checklists are added
        for (const checklist of checklists) {
            if (checklist.templateId && this._activeCaremap![supportGroupKey].checklists.some((c: IChecklist) => c.templateId === checklist.templateId)){
                throw new Error(`a checklist for templateId ${checklist.templateId} already exists for Care Map ${this._activeCaremap!.careMapId}. You must remove it in order to add this template again.`);
                // why? b/c duplicating a checklist is almost certainly not what is wanted and merging a newer version
                // of the checklist with an older version of the checklist is out of scope (for the foreseeable future)
            }
        }

        // Replace the array rather than push into it: the 3-way merge's base (lastApiResponse) can
        // share this exact array reference with _activeCaremap (the merger returns the server array
        // by reference). An in-place push would mutate base too, so the merge would see local === base,
        // conclude nothing changed locally, and drop these additions in favor of the server's copy.
        this._activeCaremap![supportGroupKey].checklists = [
            ...this._activeCaremap![supportGroupKey].checklists,
            ...checklists
        ];

        this.enqueueSave();
    };

    getSupportGroup = (supportGroupKey: ISupportGroupKey) => {
        return this._activeCaremap![supportGroupKey];
    };

    addOrUpdateSupportGroup = ({ supportGroupKey, members = [] }: { supportGroupKey: ISupportGroupKey; members?: ISupportGroupMember[] }) => {
        const isAdd = !this._activeCaremap![supportGroupKey].isRelevant;

        const supportGroup = {
            ...this._activeCaremap![supportGroupKey]!,
            members,
            isRelevant: true
        };
        if (isAdd){
            supportGroup.isRelevantSince = new Date().toISOString();
        }
        this.setSupportGroupState(supportGroupKey, supportGroup);

        this.enqueueSave();

        if (isAdd) {
            this.events.emit('CareMapState.SupportGroupAdded', supportGroupKey);
        }
    };

    removeSupportGroup = ({ supportGroupKey }: { supportGroupKey: ISupportGroupKey }) => {
        if (this._activeCaremap![supportGroupKey].members.length > 0){
            throw new Error(`cannot remove ${supportGroupKey} it has members ${JSON.stringify(this._activeCaremap![supportGroupKey].members)}`);
        }
        this.setSupportGroupState(supportGroupKey, {
            // we aren't yet explicitly deleting their notes, in part because
            // we don't want to require them to delete their notes in order to get the delete button
            // like we do members.
            // By not actually deleting notes, they are recoverable when they add the group
            // and easy for the user to then delete them if they don't want them
            ...this._activeCaremap![supportGroupKey],

            // remove checklists
            checklists: [],

            // this is what actually causes it to delete / not show up anymore
            isRelevant: false,
            isRelevantSince: undefined
        });
        this.enqueueSave();
    };

    shouldShowNameFirst = (supportGroupKey: ISupportGroupKey) => {
        return (['community', 'financial'].includes(supportGroupKey));
    };

    getChecklists = (supportGroupKey: ISupportGroupKey): IChecklist[] => {
        const checkLists = this.getCareMap()[supportGroupKey].checklists;
        return checkLists || [];
    };

    getAllSupportGroupLabels = (): {[key in ISupportGroupKey]: ISupportGroupLabels} => {
        const labels: {[key in ISupportGroupKey]: ISupportGroupLabels} = {
            'community': {
                title: 'Community',
                memberTitle: 'Community Support',
                shortMemberTitle: 'Support',
                memberNameLabel: 'Support Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__COMMUNITY__ROLE'),
                rolePlaceholder: 'Search or select support type'
            },
            'financial': {
                title: 'Financial & Legal',
                memberTitle: 'Support',
                shortMemberTitle: 'Support',
                memberNameLabel: this.labelProvider.getLabel('SUPPORT_GROUP__FINANCIAL__ROLE'),
                roleLabel: 'Support Type',
                rolePlaceholder: 'Search or select support type'
            },
            'school': {
                title: 'School',
                memberTitle: 'School Team Member',
                shortMemberTitle: 'Member',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SCHOOL__ROLE'),
                rolePlaceholder: 'Search or select member'
            },
            'medical': {
                title: 'Primary Care',
                memberTitle: 'Care Team Member',
                shortMemberTitle: 'Member',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__MEDICAL__ROLE'),
                rolePlaceholder: 'Search or select member'
            },
            'specialists': {
                title: 'Specialists',
                memberTitle: 'Specialist',
                shortMemberTitle: 'Specialist',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SPECIALISTS__ROLE'),
                rolePlaceholder: 'Search or select specialist'
            },
            'social': {
                title: 'Living',
                memberTitle: 'Support',
                shortMemberTitle: 'Support',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SOCIAL__ROLE'),
                rolePlaceholder: 'Search or select support type'
            }
        };

        return labels;
    };

    getSupportGroupLabels = (supportGroupKey: ISupportGroupKey): ISupportGroupLabels => {
        return this.getAllSupportGroupLabels()[supportGroupKey];
    };

    getSupportGroupColor = (supportGroupKey: ISupportGroupKey): PredefinedColors => {
        const keys = this.keySorter.getSortedSupportGroupKeys(this.getActiveCaremap());
        const index = keys.indexOf(supportGroupKey);
        return index%2 ? 'primary' : 'secondary';
    };

    canDeleteSupportGroup = (supportGroupKey: ISupportGroupKey): boolean => {
        return this._activeCaremap?.links.self.actions.includes('delete-support-group')
            && this._activeCaremap[supportGroupKey].members.length === 0;
    };

    hasPermissionTo = (...operations:string[]) => {
        const allowed = this._activeCaremap?.links?.self?.actions || [];
        return operations.every(o => allowed.includes(o));
    };

    getNewSupportGroupMember = ({supportGroupKey, roleId}: {supportGroupKey: ISupportGroupKey, roleId?: string}): ISupportGroupMember => {
        return this.factory.createSupportGroupMember(supportGroupKey, roleId);
    };

    cleanupContactMethods = (contactMethods: IContactMethod[]): IContactMethod[] => {
        // shouldn't have to coalesce to empty array, but wizard tests were failing without doing this
        return (contactMethods || []).filter(method => method.value);
    };

    getContactMethodOrDefault = (node: { contactMethods: IContactMethod[] }, type: IContactMethodType) => {
        return node.contactMethods?.find(m=>m.type === type) || {type: type, value: ''};
    };

    addSupportGroupMember = (supportGroupKey: ISupportGroupKey, member: ISupportGroupMember) => {
        // caller assumes enqueueSave and therefore isn/t expecting to deal with errors directly
        Promise.resolve().then(() => {
            const maxHoneycombHexagons = MAX_HONEYCOMB_SIZE;

            if (this._activeCaremap![supportGroupKey].members.length === maxHoneycombHexagons) {
                this.tracking.track('Tried to Add Too Many Members', () => ({
                    category: supportGroupKey
                }));
                const error: any = new Error(`cannot add more than ${maxHoneycombHexagons} members to the ${supportGroupKey} support group`);
                error.displayMessage = `Sorry, currently you cannot add more than ${maxHoneycombHexagons} supports to ${this.getSupportGroupLabels(supportGroupKey).title}`;
                throw error;
            }

            const members = [
                ...this._activeCaremap![supportGroupKey].members,
                {
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                }
            ];

            if (!this.findSupportGroupMember({ supportGroupKey, memberId: member.id })){
                this.addOrUpdateSupportGroup({ supportGroupKey, members });
            }
        }).catch(err => {
            this.setEnqueuedSaveError(err);
        });
    };

    private setSupportGroupState = (supportGroupKey: ISupportGroupKey, state: ISupportGroup) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            [supportGroupKey]: state
        };
        this.setState({ });
    };

    findSupportGroupMember = ({supportGroupKey, memberId}: {supportGroupKey: ISupportGroupKey, memberId: string}): ISupportGroupMember | undefined => {
        const members = this._activeCaremap![supportGroupKey].members.filter(m => m.id === memberId);
        if (members.length > 1){
            throw new Error(`found ${members.length} with memberId ${memberId}`);
        }
        return members[0];
    };

    findSupportGroupMemberByRole = ({supportGroupKey, roleId}: {supportGroupKey: ISupportGroupKey, roleId: string | undefined}): ISupportGroupMember | undefined => {
        const members = this._activeCaremap ? this._activeCaremap[supportGroupKey].members.filter(m => m.roleId === roleId) : [];
        if (members.length > 1){
            console.warn(`found ${members.length} with role ${roleId}`);
        }
        return members[0];
    };

    updateSupportGroupMember = (supportGroupKey: ISupportGroupKey, member: ISupportGroupMember) => {
        const members = this._activeCaremap![supportGroupKey].members;
        const idx = members.findIndex(item => item.id === member.id);

        members[idx] = {
            ...member,
            contactMethods: this.cleanupContactMethods(member.contactMethods)
        };

        this.setSupportGroupState(supportGroupKey, {
            ...this._activeCaremap![supportGroupKey],
            members,
        });
        this.enqueueSave();
    };

    private getIsRelevantWhenRemovingMemberWhileInWizard = ({ group, supportGroupKey }: {group: ISupportGroup; supportGroupKey: ISupportGroupKey; }): boolean => {
        if (group.members.length > 0){
            return true;
        }

        // if no members but answered yes to one of many is relevant questions
        if (supportGroupKey === 'community') {
            return this.isCommunityAdvocacyRelevant() || this.isCommunityExtracurricularRelevant();
        }

        // otherwise, removed last member, no longer relevant
        return false;
    };

    removeSupportGroupMember = ({ supportGroupKey, memberId }: {supportGroupKey: ISupportGroupKey; memberId: string; }) => {
        const group: ISupportGroup = this._activeCaremap![supportGroupKey];

        const updatedGroup: ISupportGroup = {
            ...group,
            members: group.members.filter(m => m.id !== memberId),
        };

        if (!this._activeCaremap?.essentialMapComplete){
            updatedGroup.isRelevant = this.getIsRelevantWhenRemovingMemberWhileInWizard({
                group: updatedGroup,
                supportGroupKey
            });
        }

        if (!updatedGroup.isRelevant) {
            updatedGroup.checklists = [];
        }

        this.setSupportGroupState(supportGroupKey, updatedGroup);

        this.enqueueSave();
    };

    getSupportGroupMembers = (supportGroupKey: ISupportGroupKey): ISupportGroupMember[] => {
        const supportGroup = this._activeCaremap?.[supportGroupKey];
        return supportGroup?.members || [];
    };

    // get all the members in all support groups from the care map
    getAllSupportGroupsMembers = (): ISupportGroupMember[] => {
        let members: ISupportGroupMember[] = [];

        SUPPORT_GROUP_KEYS.forEach(key => {
            const supportGroup = this._activeCaremap?.[key];
            if (supportGroup?.members) {
                members = members.concat(supportGroup.members);
            }
        });

        return members;
    };

    // get all the members in all support groups from the care map, as a map / json object
    getAllSupportGroupsMembersAsMap = (): Partial<ISupportGroupMembersMap> => {
        const members: Partial<ISupportGroupMembersMap> = {};

        SUPPORT_GROUP_KEYS.forEach(key => {
            const supportGroup = this._activeCaremap?.[key];
            if (supportGroup?.members) {
                members[key] = supportGroup.members;
            }
        });

        return members;
    };

    getSupportGroupMember = ({ supportGroupKey, memberId }: { supportGroupKey?: ISupportGroupKey, memberId: string }) => {
        let members: ISupportGroupMember[];

        if (supportGroupKey) {
            const supportGroup = this.getCareMap()[supportGroupKey];
            members = supportGroup?.members || [];
        } else {
            members = this.getAllSupportGroupsMembers();
        }

        return members.find(m => m.id === memberId);
    };

    findSupportGroupMemberCategory = (memberId: string | undefined): ISupportGroupKey | undefined => {
        let categoryId: ISupportGroupKey | undefined = undefined;
        const members = this.getAllSupportGroupsMembersAsMap();

        Object.keys(members).forEach(key => {
            if (members[key as ISupportGroupKey]?.find(member => member.id === memberId)) {
                categoryId = key as ISupportGroupKey;
            };
        });

        return categoryId;
    };

    getSupportGroupMemberInformation = (memberId: string | undefined) => {
        const member = this.getSupportGroupMember({memberId: memberId || ''});
        const categoryId = this.findSupportGroupMemberCategory(member?.id);
        const showNameFirst = this.shouldShowNameFirst(categoryId as ISupportGroupKey);
        return {member, categoryId, showNameFirst};
    };

    // Resolves a care-map member node (household or any support group) by its id, so a
    // collaborator stamped with a memberId can show its relationship tag and open the
    // right edit drawer. Returns undefined when the member no longer exists (unlinked).
    maybeGetMemberLink = (memberId: string | undefined):
        | { hiveKey: 'household'; member: IHouseholdMember; label?: string }
        | { hiveKey: ISupportGroupKey; member: ISupportGroupMember; label?: string }
        | undefined => {
        if (!memberId) {
            return undefined;
        }
        const householdMember = this.maybeGetHouseholdMember(memberId);
        if (householdMember) {
            return { hiveKey: 'household', member: householdMember, label: householdMember.relationship };
        }
        const categoryId = this.findSupportGroupMemberCategory(memberId);
        if (categoryId) {
            const member = this.getSupportGroupMember({ supportGroupKey: categoryId, memberId });
            if (member) {
                return { hiveKey: categoryId, member, label: member.role };
            }
        }
        return undefined;
    };

    // Returns a display name for any care-map member node. Household members use
    // firstName + lastName; support-group members use their single `name` field.
    // Typed structurally (not as the IHouseholdMember | ISupportGroupMember union) because
    // all three name fields are optional, so TS can't narrow the union via an `in` check —
    // reading them directly off a structural type avoids both the failed narrowing and a cast.
    getMemberDisplayName = (member: { firstName?: string; lastName?: string; name?: string }): string => {
        if (member.name) {
            return member.name;
        }
        return [member.firstName, member.lastName].filter(Boolean).join(' ');
    };

    // Resolves which sharing category a member falls under for a specific care map (not
    // necessarily the active one — used right after accepting an invite, before the care map
    // is made active). Household → Inner Circle, any support group → Community.
    getMemberCareMapCategory = (careMap: ICareMap, memberId: string | undefined): 'Inner Circle' | 'Community' | 'None' => {
        if (!memberId) {
            return 'None';
        }
        if (careMap.household.members.some(m => m.id === memberId)) {
            return 'Inner Circle';
        }
        const inSupportGroup = SUPPORT_GROUP_KEYS.some(key => careMap[key].members.some(m => m.id === memberId));
        if (inSupportGroup) {
            return 'Community';
        }
        return 'None';
    };

    // Finds an existing member whose email contact method matches (case-insensitive), so
    // an invite can link to them instead of creating a duplicate. Checks household and
    // support-group contact methods.
    findMemberByEmail = (email: string): { memberId: string } | undefined => {
        const lower = email.trim().toLowerCase();
        if (!lower) {
            return undefined;
        }
        const hasEmailContact = (contactMethods: IContactMethod[] = []): boolean =>
            contactMethods.some(c => c.type === 'email' && c.value.trim().toLowerCase() === lower);

        const household = this.getCareMap().household.members.find(m =>
            hasEmailContact(m.contactMethods)
        );
        if (household) {
            return { memberId: household.id };
        }
        const support = this.getAllSupportGroupsMembers().find(m => hasEmailContact(m.contactMethods));
        if (support) {
            return { memberId: support.id };
        }
        return undefined;
    };

    // Resolves an invite/permission back to its linked care-map member node, matching on
    // durable identity that survives delete-and-re-add (member node ids are regenerated, so
    // they can't be relied on): the accepter's userId (via linkedUserId) for an accepted
    // permission, then the contact email for a still-pending invite. Returns the same shape
    // as maybeGetMemberLink, or undefined when no member links.
    findMemberLinkForInvite = ({ email, accepterUserId }: { email?: string; accepterUserId?: string }) => {
        const byUser = accepterUserId ? this.findMemberByLinkedUserId(accepterUserId) : undefined;
        if (byUser) {
            return this.maybeGetMemberLink(byUser.memberId);
        }
        const byEmail = email ? this.findMemberByEmail(email) : undefined;
        if (byEmail) {
            return this.maybeGetMemberLink(byEmail.memberId);
        }
        return undefined;
    };

    // Finds an active-caremap member node linked to the given account (household first, then
    // support groups), so an accepted permission can be matched to its re-added member node.
    private findMemberByLinkedUserId = (userId: string): { memberId: string } | undefined => {
        if (!userId) {
            return undefined;
        }
        const household = this.getCareMap().household.members.find(m => m.linkedUserId === userId);
        if (household) {
            return { memberId: household.id };
        }
        const support = this.getAllSupportGroupsMembers().find(m => m.linkedUserId === userId);
        if (support) {
            return { memberId: support.id };
        }
        return undefined;
    };

    // Finds an existing member by full name (case-insensitive), used only to surface a
    // soft "looks like they're already on the care map" hint — never to auto-link.
    findMemberByName = ({ firstName, lastName }: { firstName: string; lastName: string }): { memberId: string; name: string } | undefined => {
        const target = `${firstName} ${lastName}`.trim().toLowerCase().replace(/\s+/g, ' ');
        if (!target) {
            return undefined;
        }
        const household = this.getCareMap().household.members.find(m =>
            `${m.firstName || ''} ${m.lastName || ''}`.trim().toLowerCase().replace(/\s+/g, ' ') === target
        );
        if (household) {
            return { memberId: household.id, name: `${household.firstName || ''} ${household.lastName || ''}`.trim() };
        }
        const support = this.getAllSupportGroupsMembers().find(m =>
            (m.name || '').trim().toLowerCase().replace(/\s+/g, ' ') === target
        );
        if (support) {
            return { memberId: support.id, name: support.name || '' };
        }
        return undefined;
    };

    // Saves an invited person as a care-map contact, filling Inner Circle (household, cap
    // MAX_HOUSEHOLD_MEMBERS) while it has room, then overflowing to Community 'general'.
    // The chosen relationship carries over (household relationship, or community *_general
    // role on overflow). Returns the new member's stable id to stamp onto the invite.
    saveContactForInvite = ({ firstName, lastName, email, relationshipId, relationship }: { firstName: string; lastName: string; email: string; relationshipId?: IRelationship; relationship?: string; }): { memberId: string; placement: 'inner-circle' | 'community' } => {
        const id = this.factory.getNodeId();
        const contactMethods: IContactMethod[] = email ? [{ type: 'email', value: email }] : [];

        if (this.getCareMap().household.members.length < MAX_HOUSEHOLD_MEMBERS) {
            const avatar = this.defaultAvatarProvider.getDefaultAvatar({ careMap: this });
            this.addHouseholdMembers([{ id, firstName, lastName, relationshipId, relationship, contactMethods, avatar }]);
            return { memberId: id, placement: 'inner-circle' };
        }

        this.addSupportGroupMember('community', {
            id,
            name: `${firstName} ${lastName}`.trim(),
            roleId: relationshipId ? `${relationshipId}_general` : undefined,
            role: relationship,
            contactMethods
        });
        return { memberId: id, placement: 'community' };
    };

    // Orchestrates the Add Person submit: optionally save the contact (linking to an
    // existing member by email, else creating one with categorization), then create the
    // invite stamped with the member id so the collaborator and member stay linked.
    invitePerson = async ({ firstName, lastName, email, access, saveContact, relationshipId, relationship }: { firstName: string; lastName: string; email: string; access: ICaremapAccess; saveContact: boolean; relationshipId?: IRelationship; relationship?: string; }): Promise<{ placement: 'inner-circle' | 'community' | 'none'; connectedToExisting: boolean; memberId?: string; }> => {
        let memberId: string | undefined;
        let placement: 'inner-circle' | 'community' | 'none' = 'none';
        let connectedToExisting = false;

        if (saveContact) {
            const existing = this.findMemberByEmail(email);
            if (existing) {
                memberId = existing.memberId;
                connectedToExisting = true;
                const link = this.maybeGetMemberLink(memberId);
                placement = link?.hiveKey === 'household' ? 'inner-circle' : 'community';
            } else {
                const saved = this.saveContactForInvite({ firstName, lastName, email, relationshipId, relationship });
                memberId = saved.memberId;
                placement = saved.placement;
            }
        }

        await this.getInviteLink({
            inviteeName: `${firstName} ${lastName}`.trim(),
            inviteeEmail: email,
            access,
            patientName: this.getActiveCaremap()?.patient?.firstName || ''
        });

        // memberId is the saved member's OWN node id, returned so the caller can open that
        // member's edit drawer. It is deliberately NOT stamped onto the invite: links are
        // matched by durable identity (email / linkedUserId), not a node id that changes on
        // delete-and-re-add.
        return { placement, connectedToExisting, memberId };
    };

    setPatientFirstName = (firstName: string) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, firstName }
        };
        this.setState({ });
        this.enqueueSave();
    };

    isUserThePatient = (): boolean => {
        const isPatientTheOwner = this._activeCaremap?.initialWizardAnswers['recipient']?.value === MYSELF.value;

        // if the caremap doesn't have a userId yet (because it is in the process of being created)
        // then this user owns it
        const ownerUserId = (this._activeCaremap as ICareMap | undefined)?.userId || this.principal.userId;

        const isUserTheOwner = ownerUserId  === this.principal.userId;

        return isPatientTheOwner && isUserTheOwner;
    };

    isMyMember = (member: IHouseholdMember | ISupportGroupMember | IPatient): boolean => {
        const email = this.userState.state.email;
        const userId = this.userState.state.userId;
        const contactEmail = member.contactMethods?.find(c => c.type === 'email')?.value;
        return Boolean(
            (userId && member.linkedUserId && member.linkedUserId === userId) ||
            (email && contactEmail && contactEmail.toLowerCase() === email.toLowerCase())
        );
    };

    isMySupportMember = (member: ISupportGroupMember): boolean => this.isMyMember(member);

    getPatientFirstName = (careMap?: ICareMap): string => {
        return (careMap ? careMap.patient.firstName : this._activeCaremap ? this._activeCaremap.patient.firstName : '') || '';
    };

    setPatientLastName = (lastName: string) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, lastName }
        };
        this.setState({ });
        this.enqueueSave();
    };

    getPatientLastName = (careMap?: ICareMap): string => {
        return (careMap ? careMap.patient.lastName : this._activeCaremap ? this._activeCaremap.patient.lastName : '') || '';
    };

    getPatientFullName = (careMap?: ICareMap): string => {
        const first = this.getPatientFirstName(careMap);
        const last = this.getPatientLastName(careMap);
        return first + (first && last ? ' ' : '') + last;
    };

    setPatientDOB = (dob: string) => {
        const dayjsDob = dayjs(dob);

        if (!dayjsDob.isValid() && dob !== '') {
            throw new Error('Invalid date for patient DOB');
        }

        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, dob }
        };
        this.setState({});
        this.enqueueSave();
    };

    getPatientDOB = (): string => {
        return this._activeCaremap!.patient.dob || '';
    };

    uploadFile = async (file: File) => {
        const entry = await this.apiClient.entry();
        const { uploadUrl, cdnUrl, headers } = await this.apiClient.post(entry.links.uploadsV2, { fileName: file.name });

        const res = await window.fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers,
        });
        if (res.status !== 200){
            throw new Error(`Upload failed with status ${res.status}`);
        }

        return { url: cdnUrl };
    };

    download = async () => {
        const filename = `${this.getPatientFullName()} Care Map ${dayjs().format('YYYY-MM-DD')}.png`;

        await this.flowDownloader.downloadFlowAsPNG({ flowName: 'full', filename });
    };

    createViewerAccessToken = async (days: number): Promise<{ access_token: string; session_token: string; }> => {
        let expiresISO8601 = dayjs().add(days, 'days').toISOString();
        if (days === .0007){
            expiresISO8601 = dayjs().add(1, 'minute').toISOString();
        }
        return await this.apiClient.post(this._activeCaremap!.links.createViewerAccessToken, {
            expiresISO8601
        });
    };

    getInviteLink = async ({ inviteeName, inviteeEmail, access, patientName }: { inviteeName: string; inviteeEmail: string; access: ICaremapAccess; patientName: string; }) => {
        const { token } = await this.apiClient.post(this._activeCaremap!.links.invites, {
            name: inviteeName,
            email: inviteeEmail,
            access
        });
        return `${this.envConfig['WEB_BASE_URL']}/#/accept-invite?invite=${encodeURIComponent(token)}&access=${access}&care-recipient=${encodeURIComponent(patientName)}`;
    };

    // internal use
    copy = {
        out: () => console.log(JSON.stringify({
            ...this.adaptCareMapForApi(this._activeCaremap!),
            careMapId: undefined,
            userId: undefined,
            links: undefined,
            version: undefined
        })),
        in: async (incomingCareMap: any) => {
            if (!incomingCareMap){
                throw new Error('incomingCareMap required');
            }
            const { careMapId, links, version } = (this._activeCaremap as any) || {};

            this._activeCaremap = { ...incomingCareMap, careMapId, links, version };
            this.setState({ });
            await this.save();

            window.location.reload();
        }
    };

    // internal use
    duplicateCareMap = async () => {
        const duplicate = {
            ...this.adaptCareMapForApi(this._activeCaremap!),
            careMapId: undefined,
            version: undefined,
            links: undefined
        };
        this._activeCaremap = duplicate;

        this.setState({});
        await this.save();
        window.location.reload();
    };

    private lastApiResponse?: ICareMap;

    getCareMap = (): ICareMap => {
        return this._activeCaremap! as ICareMap;
    };

    addCaremapToClientUser = async (user: WithLinks<IOrganizationUser>) => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank();
        body.initialWizardAnswers['recipient']  = {value: 'myself', label: 'Myself'};
        body.initialWizardAnswers['recipient-self'] = true;
        body.initialWizardAnswers['skip-steps'] = [
            'my-name', 'name',
            'recipient-self', 'recipient'
        ];

        body.patient.firstName = user.firstName;
        body.patient.lastName = user.lastName;

        const result = await this.apiClient.post(user.links.caremaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        return { careMapId: result.careMapId };
    };

    addNewCaremapV2 = async () => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank();

        const entry = await this.apiClient.entry();

        const result = await this.apiClient.post(entry.links.careMaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        return { careMapId: result.careMapId };
    };

    addNewCaremapV3 = async ({
        patient,
        isRecipientSelf,
        householdMembers = [],
        isAutoCreated,
    }: {
        patient: Partial<IPatient>;
        isRecipientSelf: boolean;
        householdMembers?: IHouseholdMember[];
        isAutoCreated?: boolean;
    }) => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank(true);

        body.patient = {
            ...body.patient,
            ...patient
        };

        body.household.members = [...body.household.members, ...householdMembers];

        body.initialWizardAnswers = {
            // there is some old coupling to this that isn't worth addressing at this time
            // so just adapting to look the way we expect
            'recipient': isRecipientSelf ? MYSELF : undefined,
            'recipient-self': isRecipientSelf,
            [isRecipientSelf ? 'consent-self-date' : 'consent-someone-else-date'] : new Date().toISOString(),
            'onboarding/add-care-recipient/started': new Date().toISOString()
        };

        body.essentialMapComplete = isAutoCreated ? false : true;

        const entry = await this.apiClient.entry();

        const result = await this.apiClient.post(entry.links.careMaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        // can only have one care map that is 'self'
        if (isRecipientSelf) {
            this.events.emit('MixPanelService.set-profile-prop', {
                prop: 'Care Recipient Profiles',
                val: 1
            });
        }

        return { careMapId: result.careMapId };
    };

    removeMe = async () => {
        const activeCareMap = this.getActiveCaremap();

        if(!activeCareMap){
            throw new Error('expected activeCareMap');
        }

        if (!activeCareMap.links.removeSelf){
            throw new Error('expected activeCareMap.links.removeSelf');
        }

        await this.apiClient.delete(activeCareMap.links.removeSelf);

        // home so it can decide where to go (maybe org, maybe caregiver)
        this.history.push('/');

        this.careMaps = this.careMaps.filter(c => c.careMapId !== activeCareMap.careMapId);
        this.lastActiveCareMapId = undefined;
        await this.lastActiveCareMapIdStorage.remove();
        this.unsetCurrentCareMap();

        // Same invariant as afterDelete — keep a blank around so chat always has context.
        this.ensureCareMapExists().catch(err => {
            this.logger.error(`removeSelf: ensureCareMapExists failed: ${err?.toString?.() ?? err}`);
        });
    };

    removeOrgClient = async (user: IOrganizationUser) => {
        const userCaremaps = this.careMaps.filter(c => c.userId !== user.userId);
        const userCaremapIds = userCaremaps.map(c => c.careMapId);

        const caremaps = this.careMaps.filter(c => c.userId !== user.userId);
        if (caremaps.length < this.careMaps.length){
            this.careMaps = caremaps;
        }

        if (userCaremapIds.includes(this.lastActiveCareMapId || '')){
            this.lastActiveCareMapId = undefined;
            await this.lastActiveCareMapIdStorage.remove();
        }

        if (userCaremapIds.includes(this._activeCaremap?.careMapId || '')){
            await this.unsetCurrentCareMap();
        }

        this.setState({ });
    };

    hasStartedOnboardingStep = (step: ICareMapOnboardingStep) => {
        return Boolean(this.getInitialWizardAnswer(`onboarding/${step}/started`));
    };

    hasCompletedOnboardingStep = (step: ICareMapOnboardingStep) => {
        return Boolean(this.getInitialWizardAnswer(`onboarding/${step}/completed`)) || false;
    };

    isInOnboardingStep = (step: ICareMapOnboardingStep) => {
        return this.hasStartedOnboardingStep(step) && !this.hasCompletedOnboardingStep(step);
    };

    startOnboardingStep = (step: ICareMapOnboardingStep) => {
        this.answerInitialWizardQuestion({
            questionId: `onboarding/${step}/started`,
            answer: new Date().toISOString()
        });
    };

    completeOnboardingStep = (step: ICareMapOnboardingStep) => {
        this.answerInitialWizardQuestion({
            questionId: `onboarding/${step}/completed`,
            answer: new Date().toISOString()
        });
    };

    isPatientIncomplete = (options: { excludeDOB?: boolean } = {}) => {
        const patient = this.getActiveCaremap()!.patient;
        return (
            !patient.diagnoses?.length &&
            !patient.allergies?.length &&
            !patient.contactMethods.length &&
            !patient.address &&
            (options?.excludeDOB || !patient.dob)
        );
    };

};

export type ICareMapOnboardingStep =
    // this is where they get a chance to add an avatar
    // and in the future (add caregiver to household)
    'add-care-recipient' | 'household-members' | 'add-note' |

    // NOTE: these can be dynamically built like in Checklists.tsx
    'started-getting-started' | 'started-estate-planning' | 'started-advance-care';
